/**
 * Obraven header behaviour (sections/header.liquid):
 * - <sticky-header>: sticky modes, --header-height, hide on scroll down ("on-scroll-up").
 * - <ob-header-nav>: desktop dropdowns and mega menus (hover with a mouse, click, Esc, focus-out),
 *   with a short hover intent, an animated open/close and an in-place swap between open menus.
 * - <ob-menu-drawer>: the mobile/tablet menu drawer (modal dialog, tabs, sub-panels).
 * - <ob-search-modal>: the search overlay opened by the search icons.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');

  /** Runs `callback` once the element's transition ends (or straight away under reduced motion). */
  const afterTransition = (element, callback, duration = 500) => {
    if (reducedMotion.matches) {
      callback();
      return;
    }
    let done = false;
    const finish = (event) => {
      if (done || (event && event.target !== element)) return;
      done = true;
      element.removeEventListener('transitionend', finish);
      callback();
    };
    element.addEventListener('transitionend', finish);
    window.setTimeout(finish, duration);
  };

  /** Two animation frames: lets a just-shown element paint its start state before transitioning. */
  const nextFrame = (callback) => requestAnimationFrame(() => requestAnimationFrame(callback));

  const lockScroll = () => document.body.classList.add('ob-scroll-lock');
  const unlockScroll = () => document.body.classList.remove('ob-scroll-lock');

  /* ---------- Sticky header ---------- */

  if (!customElements.get('sticky-header')) {
    customElements.define(
      'sticky-header',
      class StickyHeader extends HTMLElement {
        connectedCallback() {
          this.header = this.closest('.section-header') || this;
          this.type = this.getAttribute('data-sticky-type') || 'none';
          this.currentScrollTop = 0;
          this.preventReveal = false;
          this.predictiveSearches = this.querySelectorAll('predictive-search');

          this.setHeaderHeight = this.setHeaderHeight.bind(this);
          this.setHeaderHeight();
          this.resizeObserver = new ResizeObserver(this.setHeaderHeight);
          this.groupSections().forEach((section) => this.resizeObserver.observe(section));

          if (this.type === 'none') return;
          if (this.type === 'always') this.header.classList.add('shopify-section-header-sticky');

          this.onScrollHandler = this.onScroll.bind(this);
          this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
          this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
          window.addEventListener('scroll', this.onScrollHandler, { passive: true });
        }

        disconnectedCallback() {
          this.resizeObserver?.disconnect();
          if (this.onScrollHandler) window.removeEventListener('scroll', this.onScrollHandler);
          if (this.hideHeaderOnScrollUp) this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        }

        // The header group (announcement bar + header), or just the header outside the group.
        groupSections() {
          const sections = document.querySelectorAll('.shopify-section-group-header-group');
          return sections.length ? [...sections] : [this.header];
        }

        // --header-height: the sticky header; --ob-header-group-height: everything above <main>
        // (screen-fitting sections in theme.css use both).
        setHeaderHeight() {
          const groupHeight = this.groupSections().reduce((sum, section) => sum + section.offsetHeight, 0);
          document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
          document.documentElement.style.setProperty('--ob-header-group-height', `${groupHeight}px`);
        }

        onScroll() {
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const headerBottom = this.header.offsetTop + this.header.offsetHeight;

          if ([...this.predictiveSearches].some((search) => search.isOpen)) return;

          if (scrollTop > this.currentScrollTop && scrollTop > headerBottom) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventHide) requestAnimationFrame(() => this.hide());
          } else if (scrollTop < this.currentScrollTop && scrollTop > headerBottom) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
              requestAnimationFrame(() => this.reveal());
            } else {
              window.clearTimeout(this.isScrolling);
              this.isScrolling = window.setTimeout(() => (this.preventReveal = false), 66);
              requestAnimationFrame(() => this.hide());
            }
          } else if (scrollTop <= this.header.offsetTop) {
            this.header.classList.remove('scrolled-past-header');
            requestAnimationFrame(() => this.reset());
          }

          this.currentScrollTop = scrollTop;
        }

        hide() {
          if (this.type !== 'on-scroll-up') return;
          this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
          this.querySelectorAll('ob-header-nav').forEach((nav) => nav.close());
        }

        reveal() {
          if (this.type !== 'on-scroll-up') return;
          this.header.classList.add('shopify-section-header-sticky', 'animate');
          this.header.classList.remove('shopify-section-header-hidden');
        }

        reset() {
          if (this.type !== 'on-scroll-up') return;
          this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
        }
      }
    );
  }

  /* ---------- Desktop navigation: dropdowns and mega menus ---------- */

  // Hover intent before the first menu opens (crossing the bar shouldn't flash menus open), and a grace
  // period before closing when the pointer slips past the header's edge.
  const NAV_OPEN_DELAY = 90;
  const NAV_CLOSE_DELAY = 160;

  if (!customElements.get('ob-header-nav')) {
    customElements.define(
      'ob-header-nav',
      class HeaderNav extends HTMLElement {
        connectedCallback() {
          this.header = this.closest('header') || this;
          this.openItem = null;

          this.onPointerOver = this.onPointerOver.bind(this);
          this.onHeaderEnter = () => window.clearTimeout(this.leaveTimer);
          this.onHeaderLeave = this.onHeaderLeave.bind(this);
          this.onDocumentClick = this.onDocumentClick.bind(this);

          this.addEventListener('click', this.onClick.bind(this));
          this.addEventListener('keydown', this.onKeyDown.bind(this));
          this.addEventListener('focusout', this.onFocusOut.bind(this));
          this.addEventListener('pointerover', this.onPointerOver);
          this.header.addEventListener('pointerenter', this.onHeaderEnter);
          this.header.addEventListener('pointerleave', this.onHeaderLeave);
          document.addEventListener('click', this.onDocumentClick);
          desktop.addEventListener('change', () => this.close({ immediate: true }));

          if (window.Shopify && Shopify.designMode) {
            document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event));
            document.addEventListener('shopify:block:deselect', () => this.close());
          }
        }

        disconnectedCallback() {
          this.header?.removeEventListener('pointerenter', this.onHeaderEnter);
          this.header?.removeEventListener('pointerleave', this.onHeaderLeave);
          document.removeEventListener('click', this.onDocumentClick);
        }

        getParts(item) {
          return {
            trigger: item.querySelector('[data-nav-trigger]'),
            panel: item.querySelector('[data-nav-panel]'),
          };
        }

        // Panels move through data-state closed -> open -> closing (CSS animates opacity/transform).
        open(item) {
          window.clearTimeout(this.hoverTimer);
          window.clearTimeout(this.leaveTimer);
          if (this.openItem === item) return;
          // With a menu already showing, the next one swaps in place (data-switch: no drop, content fades).
          const showing = this.openItem || this.querySelector('[data-nav-panel][data-state="closing"]');
          this.close({ immediate: true });
          this.querySelectorAll('[data-nav-panel][data-state="closing"]').forEach((other) => this.hidePanel(other));
          this.openedByHover = false;
          const { trigger, panel } = this.getParts(item);
          if (!trigger || !panel) return;
          const headerBottom = this.header.getBoundingClientRect().bottom;
          panel.style.setProperty('--ob-nav-panel-max-height', `${Math.max(240, window.innerHeight - headerBottom)}px`);
          this.header.style.setProperty('--ob-nav-backdrop-top', `${headerBottom}px`);
          panel.toggleAttribute('data-switch', Boolean(showing));
          panel.dataset.state = 'closed';
          panel.hidden = false;
          nextFrame(() => {
            if (this.openItem === item) panel.dataset.state = 'open';
          });
          trigger.setAttribute('aria-expanded', 'true');
          this.header.classList.add('is-nav-open');
          this.openItem = item;
          const sticky = this.closest('sticky-header');
          if (sticky) sticky.preventHide = true;
        }

        close({ focusTrigger = false, immediate = false } = {}) {
          window.clearTimeout(this.hoverTimer);
          window.clearTimeout(this.leaveTimer);
          const item = this.openItem;
          if (!item) return;
          const { trigger, panel } = this.getParts(item);
          trigger.setAttribute('aria-expanded', 'false');
          this.header.classList.remove('is-nav-open');
          this.openItem = null;
          if (immediate) {
            this.hidePanel(panel);
          } else {
            panel.dataset.state = 'closing';
            afterTransition(
              panel,
              () => {
                if (panel.dataset.state === 'closing') this.hidePanel(panel);
              },
              260
            );
          }
          const sticky = this.closest('sticky-header');
          if (sticky) sticky.preventHide = false;
          if (focusTrigger) trigger.focus();
        }

        hidePanel(panel) {
          panel.hidden = true;
          delete panel.dataset.state;
          panel.removeAttribute('data-switch');
        }

        onClick(event) {
          const trigger = event.target.closest('[data-nav-trigger]');
          if (!trigger) return;
          const item = trigger.closest('[data-nav-item]');
          if (this.openItem !== item) {
            this.open(item);
          } else if (this.openedByHover) {
            // A click on a menu the pointer just opened keeps it open instead of toggling it shut.
            this.openedByHover = false;
          } else {
            this.close();
          }
        }

        onPointerOver(event) {
          if (event.pointerType !== 'mouse' || !desktop.matches) return;
          window.clearTimeout(this.leaveTimer);
          window.clearTimeout(this.hoverTimer);
          const trigger = event.target.closest('[data-nav-trigger]');
          if (trigger) {
            const item = trigger.closest('[data-nav-item]');
            if (this.openItem === item) return;
            // Once a menu is open, moving to the next is instant; the first one waits for hover intent.
            this.hoverTimer = window.setTimeout(
              () => {
                this.open(item);
                this.openedByHover = true;
              },
              this.openItem ? 0 : NAV_OPEN_DELAY
            );
            return;
          }
          if (event.target.closest('[data-nav-plain]')) this.close();
        }

        onHeaderLeave(event) {
          if (event.pointerType !== 'mouse') return;
          window.clearTimeout(this.hoverTimer);
          this.leaveTimer = window.setTimeout(() => this.close(), NAV_CLOSE_DELAY);
        }

        onDocumentClick(event) {
          if (this.openItem && !this.openItem.contains(event.target)) this.close();
        }

        onKeyDown(event) {
          if (event.key === 'Escape' && this.openItem) {
            event.preventDefault();
            this.close({ focusTrigger: true });
            return;
          }

          const trigger = event.target.closest('[data-nav-trigger]');
          if (trigger && event.key === 'ArrowDown') {
            event.preventDefault();
            const item = trigger.closest('[data-nav-item]');
            this.open(item);
            item.querySelector('[data-nav-panel] a')?.focus();
          }
        }

        onFocusOut(event) {
          if (!this.openItem) return;
          if (event.relatedTarget && this.openItem.contains(event.relatedTarget)) return;
          // relatedTarget is null when focus leaves the page or lands on a non-focusable element.
          window.setTimeout(() => {
            if (this.openItem && !this.openItem.contains(document.activeElement)) this.close();
          });
        }

        onBlockSelect(event) {
          const item = event.target.closest('[data-nav-item]');
          if (item && this.contains(item)) this.open(item);
        }
      }
    );
  }

  /* ---------- Shared modal behaviour for the drawer and the search overlay ---------- */

  class ModalDialogElement extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.onEscape();
      });
      this.dialog.addEventListener('close', () => this.onDialogClosed());
      // A click on the ::backdrop targets the dialog itself, outside its box.
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) this.close();
      });
    }

    get isOpen() {
      return this.dialog.open;
    }

    open(trigger) {
      if (this.dialog.open) return;
      this.trigger = trigger || document.activeElement;
      this.dialog.showModal();
      lockScroll();
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.onOpen();
      nextFrame(() => this.dialog.classList.add('is-open'));
    }

    close() {
      if (!this.dialog.open || this.closing) return;
      this.closing = true;
      this.dialog.classList.remove('is-open');
      afterTransition(this.animatedElement || this.dialog, () => this.dialog.close());
    }

    /** Runs on the dialog's `close` event, however it was closed (our close() or the browser). */
    onDialogClosed() {
      this.closing = false;
      this.dialog.classList.remove('is-open');
      unlockScroll();
      this.onClose();
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.trigger?.isConnected) this.trigger.focus();
    }

    onEscape() {
      this.close();
    }

    onOpen() {}

    onClose() {}
  }

  /* ---------- Menu drawer ---------- */

  if (!customElements.get('ob-menu-drawer')) {
    customElements.define(
      'ob-menu-drawer',
      class MenuDrawer extends ModalDialogElement {
        connectedCallback() {
          super.connectedCallback();
          if (!this.dialog) return;

          this.body = this.dialog.querySelector('[data-drawer-body]');
          this.foot = this.dialog.querySelector('[data-drawer-foot]');
          this.tabs = Array.from(this.dialog.querySelectorAll('[data-drawer-tab]'));

          this.querySelectorAll('[data-drawer-open]').forEach((button) =>
            button.addEventListener('click', () => this.open(button))
          );
          this.dialog.querySelectorAll('[data-drawer-close]').forEach((button) =>
            button.addEventListener('click', () => this.close())
          );
          this.dialog.querySelectorAll('[data-submenu-open]').forEach((button) =>
            button.addEventListener('click', () => this.openSubmenu(button))
          );
          this.dialog.querySelectorAll('[data-submenu-close]').forEach((button) =>
            button.addEventListener('click', () => this.closeSubmenu())
          );
          this.tabs.forEach((tab) => {
            tab.addEventListener('click', () => this.selectTab(tab));
            tab.addEventListener('keydown', (event) => this.onTabKeyDown(event));
          });

          // The drawer belongs to the mobile/tablet layout (its breakpoint depends on the header layout):
          // close it once the window grows enough to hide the menu button.
          this.toggle = this.querySelector('[data-drawer-open]');
          this.onResize = () => {
            if (this.dialog.open && this.toggle && this.toggle.getClientRects().length === 0) this.close();
          };
          window.addEventListener('resize', this.onResize, { passive: true });
        }

        onOpen() {
          this.dialog.querySelector('[data-drawer-close]')?.focus();
        }

        onClose() {
          this.closeSubmenu({ instant: true });
        }

        onEscape() {
          // Esc closes an open country picker first, then a sub-panel, then the drawer.
          const picker = this.dialog.querySelector('.localization-form__select[aria-expanded="true"]');
          const pickerForm = picker?.closest('localization-form');
          if (pickerForm && typeof pickerForm.hidePanel === 'function') {
            pickerForm.hidePanel();
            picker.focus();
          } else if (this.submenu) {
            this.closeSubmenu();
          } else {
            this.close();
          }
        }

        selectTab(tab, focus = false) {
          this.tabs.forEach((other) => {
            const selected = other === tab;
            other.setAttribute('aria-selected', String(selected));
            other.tabIndex = selected ? 0 : -1;
            const panel = document.getElementById(other.getAttribute('aria-controls'));
            if (panel) panel.hidden = !selected;
          });
          if (focus) tab.focus();
        }

        onTabKeyDown(event) {
          const index = this.tabs.indexOf(event.currentTarget);
          let next = null;
          switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              next = this.tabs[(index + 1) % this.tabs.length];
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
              break;
            case 'Home':
              next = this.tabs[0];
              break;
            case 'End':
              next = this.tabs[this.tabs.length - 1];
              break;
            default:
              return;
          }
          event.preventDefault();
          this.selectTab(next, true);
        }

        openSubmenu(button) {
          const panel = document.getElementById(button.getAttribute('aria-controls'));
          if (!panel) return;
          this.submenu = { panel, button };
          panel.hidden = false;
          button.setAttribute('aria-expanded', 'true');
          if (this.body) this.body.inert = true;
          if (this.foot) this.foot.inert = true;
          nextFrame(() => {
            panel.classList.add('is-open');
            panel.querySelector('[data-submenu-close]')?.focus();
          });
        }

        closeSubmenu({ instant = false } = {}) {
          if (!this.submenu) return;
          const { panel, button } = this.submenu;
          this.submenu = null;
          panel.classList.remove('is-open');
          button.setAttribute('aria-expanded', 'false');
          if (this.body) this.body.inert = false;
          if (this.foot) this.foot.inert = false;
          const finish = () => {
            if (!panel.classList.contains('is-open')) panel.hidden = true;
          };
          if (instant) {
            finish();
          } else {
            afterTransition(panel, finish);
            button.focus();
          }
        }
      }
    );
  }

  /* ---------- Search overlay ---------- */

  if (!customElements.get('ob-search-modal')) {
    customElements.define(
      'ob-search-modal',
      class SearchModal extends ModalDialogElement {
        connectedCallback() {
          super.connectedCallback();
          if (!this.dialog) return;

          this.input = this.dialog.querySelector('input[type="search"]');
          this.animatedElement = this.dialog.querySelector('.ob-search-modal__panel');
          document
            .querySelectorAll(`[data-search-open][aria-controls="${this.dialog.id}"]`)
            .forEach((button) => button.addEventListener('click', () => this.open(button)));
          this.dialog
            .querySelector('[data-search-close]')
            ?.addEventListener('click', () => this.close());
        }

        onOpen() {
          if (!this.input) return;
          this.input.focus();
          // Put the caret after any existing term.
          const length = this.input.value.length;
          this.input.setSelectionRange(length, length);
        }

        onClose() {
          const predictiveSearch = this.dialog.querySelector('predictive-search');
          if (typeof predictiveSearch?.close === 'function') predictiveSearch.close();
        }
      }
    );
  }

  /* ---------- Customer account sheet position ---------- */

  // The account `open` event is composed but does not bubble, so listen during capture, and anchor the
  // sign-in sheet to the bottom of the account button.
  document.addEventListener(
    'open',
    (event) => {
      if (!(event.target instanceof HTMLElement) || !event.target.matches('shopify-account')) return;
      const bottom = Math.max(0, Math.round(event.target.getBoundingClientRect().bottom));
      event.target.style.setProperty('--account-dialog-top', `${bottom + 8}px`);
    },
    true
  );
})();
